import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, AlertTriangle, XCircle, ShieldAlert, RefreshCw, Info,
  FileText, TrendingUp, Gauge, Loader2, HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { loanProfilingApi } from '@/services/api';

const VERDICT = {
  STRONG: { label: 'Strong', tag: 'Pursue', icon: CheckCircle2,
    ring: 'border-emerald-300', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  NEEDS_WORK: { label: 'Needs Work', tag: 'Pursue with conditions', icon: AlertTriangle,
    ring: 'border-amber-300', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  DIFFICULT: { label: 'Difficult', tag: 'Hard case', icon: XCircle,
    ring: 'border-rose-300', bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
};
const SEV = {
  blocker: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: ShieldAlert, label: 'Blocker' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle, label: 'Review' },
  positive: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2, label: 'Strength' },
  info: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', icon: Info, label: 'Note' },
};
const GRADE = { A: 'bg-emerald-600', B: 'bg-lime-600', C: 'bg-amber-500', D: 'bg-rose-600' };

const rupees = (n) => (n || n === 0) ? `₹${Math.round(n).toLocaleString('en-IN')}` : '—';
const pct = (n) => (n || n === 0) ? `${n}%` : '—';

const Metric = ({ label, value, hint, warn }) => (
  <div className={`rounded-lg border p-3 ${warn ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200 bg-white'}`}
       data-testid={`uw-metric-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</div>
    <div className={`text-lg font-semibold ${warn ? 'text-amber-700' : 'text-slate-900'}`}>{value}</div>
    {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
  </div>
);

export const UnderwritingReadiness = ({ leadId, reloadKey, hideVerdictHeader = false, data: provided }) => {
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(!provided);
  const [busy, setBusy] = useState(false);
  const data = provided || fetched;

  const load = useCallback(async (manual = false) => {
    if (provided) return;
    try {
      manual ? setBusy(true) : setLoading(true);
      const res = await loanProfilingApi.underwriting(leadId);
      setFetched(res.data);
      if (manual) toast.success('Underwriting readiness recalculated');
    } catch (e) {
      if (manual) toast.error('Could not compute readiness');
    } finally {
      setLoading(false); setBusy(false);
    }
  }, [leadId, provided]);

  useEffect(() => { load(); }, [load, reloadKey]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-6 border border-slate-200 rounded-xl mb-6"
           data-testid="uw-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Computing loan readiness…
      </div>
    );
  }
  if (!data?.verdict) return null;

  const v = data.verdict;
  const m = data.metrics || {};
  const vc = VERDICT[v.verdict] || VERDICT.NEEDS_WORK;
  const VIcon = vc.icon;
  const reasons = v.reason_codes || [];
  const vi = data.verdict_inputs;
  const followUp = (data.documents?.items || []).filter((d) => d.stage !== 'verdict_input' && d.required && !d.collected);

  return (
    <div className="mb-6" data-testid="underwriting-readiness">
      {!hideVerdictHeader && (
      <>
      {/* Verdict header */}
      <div className={`rounded-xl border-2 ${vc.ring} ${vc.bg} p-5`} data-testid="uw-verdict-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <VIcon className={`w-8 h-8 ${vc.text} shrink-0`} />
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-xl font-bold ${vc.text}`} data-testid="uw-verdict-label">{vc.label}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full bg-white ${vc.text}`}>{vc.tag}</span>
                <span className={`text-xs font-bold text-white px-2 py-0.5 rounded ${GRADE[v.risk_grade] || 'bg-slate-500'}`}
                      data-testid="uw-risk-grade">Grade {v.risk_grade}</span>
                {vi && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${vi.basis === 'firm' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                        data-testid="uw-verdict-basis">
                    {vi.basis === 'firm' ? 'Firm' : `Provisional · ${vi.missing_labels.length} input${vi.missing_labels.length === 1 ? '' : 's'} missing`}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-700 mt-1 max-w-xl" data-testid="uw-action">{v.action}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900" data-testid="uw-score">{v.score}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wide">Readiness / 100</div>
            </div>
            <button onClick={() => load(true)} disabled={busy}
                    className="flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-300 rounded-lg px-3 py-2 hover:bg-white transition-colors"
                    data-testid="uw-recalc-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Recalculate
            </button>
          </div>
        </div>
        {!data.has_bureau && (
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 bg-white/60 rounded-lg px-3 py-2">
            <Info className="w-3.5 h-3.5" /> CIBIL not pulled yet — pull the bureau report for a full assessment.
          </div>
        )}
      </div>

      {/* Metrics — what the bank will compute */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="uw-metrics">
        <Metric label="FOIR" value={pct(m.foir)} hint={`cap ${data.policy_summary?.foir_max}%`}
                warn={m.foir != null && m.foir > (data.policy_summary?.foir_max || 50)} />
        <Metric label="DTI" value={pct(m.dti)} hint="existing debt burden" />
        <Metric label="LTV" value={pct(m.ltv)} hint={m.ltv_ceiling ? `ceiling ${m.ltv_ceiling}%` : 'add valuation'}
                warn={m.ltv != null && m.ltv_ceiling != null && m.ltv > m.ltv_ceiling} />
        <Metric label="DSCR" value={m.dscr ?? '—'} hint={`min ${data.policy_summary?.dscr_min}`}
                warn={m.dscr != null && m.dscr < (data.policy_summary?.dscr_min || 1.2)} />
        <Metric label="Eligible Loan" value={rupees(m.required_loan_amount)} hint="requested" />
        <Metric label="Indicative Rate" value={m.indicative_rate ? `${m.indicative_rate}%` : '—'} hint="by CIBIL band" />
        <Metric label="Est. EMI" value={rupees(m.proposed_emi)} hint={`${m.tenure_months || '—'} mo`} />
        <Metric label="CIBIL" value={v.cibil_score || (data.has_bureau ? '—' : 'Not pulled')} hint="bureau score" />
      </div>
      </>
      )}

      {/* Reason codes — the explainable "why" */}
      {reasons.length > 0 && (
        <div className="mt-5" data-testid="uw-reasons">
          <div className="flex items-center gap-2 mb-2">
            <Gauge className="w-4 h-4 text-slate-400" />
            <h4 className="text-sm font-semibold text-slate-700">Why this verdict</h4>
          </div>
          <div className="space-y-2">
            {reasons.map((r, i) => {
              const s = SEV[r.severity] || SEV.info;
              const SIcon = s.icon;
              return (
                <div key={`${r.code}-${i}`} className={`flex items-start gap-3 rounded-lg border ${s.border} ${s.bg} p-3`}
                     data-testid={`uw-reason-${r.code}`}>
                  <SIcon className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${s.text}`}>{r.title}</span>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${s.bg} ${s.text} border ${s.border}`}>{s.label}</span>
                    </div>
                    {r.detail && <p className="text-xs text-slate-600 mt-0.5">{r.detail}</p>}
                    {r.action && <p className="text-xs text-slate-500 mt-0.5"><span className="font-medium">Do:</span> {r.action}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ① Documents needed FOR the verdict (the basic up-front set) */}
      {vi && (
        <div className="mt-5" data-testid="uw-verdict-inputs">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-slate-400" />
            <h4 className="text-sm font-semibold text-slate-700">① Needed for the verdict</h4>
            <span className="text-xs font-normal text-slate-400">the basic set the verdict is computed from</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {vi.items.map((it) => (
              <div key={it.key}
                   className={`flex items-start gap-2 rounded-lg border p-3 ${it.present ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}
                   data-testid={`uw-input-${it.key}`}>
                {it.present
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  : <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800">{it.label}</div>
                  <div className="text-[11px] text-slate-500">from: {it.source}</div>
                  <div className={`text-xs mt-0.5 ${it.present ? 'text-emerald-700' : 'text-amber-700'}`}>{it.detail}</div>
                </div>
              </div>
            ))}
          </div>
          {vi.basis === 'provisional' && (
            <p className="text-xs text-amber-700 mt-2">
              Verdict is provisional until these are in — collect the missing items above for a firm result.
            </p>
          )}
        </div>
      )}

      {/* ② Documents needed BASED ON the verdict */}
      <div className="mt-5" data-testid="uw-followup-docs">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-slate-400" />
          <h4 className="text-sm font-semibold text-slate-700">② Needed based on the verdict</h4>
          <span className="text-xs font-normal text-slate-400">
            {data.documents?.collected}/{data.documents?.total} collected · {followUp.length} still needed
          </span>
        </div>
        {followUp.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3"
               data-testid="uw-docs-clean">
            <CheckCircle2 className="w-4 h-4" /> Nothing else needed right now — ready for the bank once the basic set is in.
          </div>
        ) : (
          <div className="space-y-4">
            {[['risk', 'Because of the verdict'], ['bank_submission', 'For bank submission (KYC & file)']].map(([stage, title]) => {
              const list = followUp.filter((d) => d.stage === stage);
              if (!list.length) return null;
              return (
                <div key={stage} data-testid={`uw-docgroup-${stage}`}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{title}</div>
                  <div className="space-y-2">
                    {list.map((d) => (
                      <div key={d.doc_type} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`uw-doc-${d.doc_type}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-800">{d.label}</span>
                          {d.stage === 'risk' && (
                            <span className="text-[10px] font-bold uppercase text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">Risk</span>
                          )}
                        </div>
                        {(d.why || []).slice(0, 1).map((w, i) => (
                          <p key={i} className="text-xs text-slate-500 mt-0.5">{w}</p>
                        ))}
                        {d.pre_empts_query && (
                          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <HelpCircle className="w-3 h-3" /> {d.pre_empts_query}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnderwritingReadiness;
